import qs from 'qs';
import type { AxiosResponse } from 'axios';

import { API_URL } from '@/config/env';
import { conformanceApi } from '@/lib/api';
import type {
  Checkpoint,
  CheckpointSystemStatus,
  SystemCheckpointConfiguration,
  Evaluation,
  EvaluationAssessmentDataItem,
  EvaluationCheckpoint,
  EvaluationStatusResponse,
  IObject,
  ReleaseNotePage,
  ReleaseNoteUpsertRequest,
  TriggerEvaluationRequest,
  UserRoleAccess,
} from '@/types';
import type {
  ApplicationConfiguration,
  CheckpointParametersRequest,
  EvaluationsResponse,
} from '@/modules/application/types';
import { getToken } from '@/lib/utils';
import { conformanceRequest } from '@/config/msalConfig';
import { hasCheckpointVersionValue } from '@/lib/checkpointKeys';

const normalizeCheckpoint = (
  checkpoint: EvaluationCheckpoint,
): EvaluationCheckpoint => {
  return {
    ...checkpoint,
    items: Array.isArray(checkpoint.items) ? checkpoint.items : [],
    assessmentData: Array.isArray(checkpoint.assessmentData)
      ? checkpoint.assessmentData
      : [],
    exceptionDetails: checkpoint.exceptionDetails ?? null,
  };
};

export const fetchEvaluations = (filters: IObject) => {
  return conformanceApi.get<EvaluationsResponse>(`${API_URL}/evaluations`, {
    params: { ...filters },
    paramsSerializer: (params) => {
      return qs.stringify(params, { arrayFormat: 'repeat', encode: false });
    },
  });
};

export const exportEvaluationsCsv = (filters: IObject) => {
  return conformanceApi.get<Blob>(`${API_URL}/evaluations/export`, {
    params: { ...filters },
    paramsSerializer: (params) => {
      return qs.stringify(params, { arrayFormat: 'repeat', encode: false });
    },
    responseType: 'blob',
  });
};

export const fetchEvaluationExcel = async (
  evaluationId: string,
  view: string = 'default',
) => {
  const token = await getToken(conformanceRequest);
  return fetch(
    `${API_URL}/jobs/results/${evaluationId}?${new URLSearchParams({
      Format: 'Excel',
      view,
    })}`,
    {
      method: 'GET',
      headers: {
        authorization: `Bearer ${token}`,
        accept:
          'application/json, application/vnd.openxmlformats-officedocument.spreadsheet, text/plain, */*',
      },
    },
  );
};

export const fetchCheckpointAssessment = (
  recordId: string,
  checkpointId: string,
  checkpointVersion?: string | number | null,
) => {
  const hasCheckpointVersion = hasCheckpointVersionValue(checkpointVersion);

  return conformanceApi.get<{
    data: EvaluationAssessmentDataItem[];
  }>(`${API_URL}/evaluations/${recordId}/assessments/${checkpointId}`, {
    params: hasCheckpointVersion
      ? {
          CheckpointVersion: `${checkpointVersion}`,
        }
      : undefined,
  });
};

export const fetchApplicationConfigurations = async (bitId: string) => {
  const response = await conformanceApi.get<{
    data: ApplicationConfiguration[];
    message: string;
    status: string;
  }>(`${API_URL}/${bitId}/configurations`);

  return response.data.data;
};

export const triggerEvaluation = ({
  bitId,
  ...rest
}: TriggerEvaluationRequest) => {
  return conformanceApi.post<{ jobId: string; statusUrl: string }>(
    `${API_URL}/jobs/${bitId}`,
    rest,
    {
      headers: {
        'x-orr-automation-platform': 'ORR Portal',
      },
    },
  );
};

export const getEvaluationJobStatus = (
  jobId: string,
) => {
  return conformanceApi.get<EvaluationStatusResponse>(
    `${API_URL}/jobs/${jobId}/status`,
  );
};

export const getUserRoleAccess = () => {
  return conformanceApi.get<UserRoleAccess>(
    `${API_URL.replace('/orr', '')}/system/user`,
  );
};

export const getCheckpoints = () => {
  return conformanceApi.get<{
    message: string;
    status: string;
    data: Checkpoint[];
  }>(`${API_URL}/checkpoints`);
};

const getConformanceSwaggerUiJsonUrl = () => {
  const normalizedApiUrl = API_URL.replace(/\/+$/, '');
  const versionMatch = normalizedApiUrl.match(/\/api\/(v[^/]+)/i);
  const swaggerVersion = versionMatch?.[1] ?? 'v4.0';

  try {
    const parsed = new URL(normalizedApiUrl);
    return `${parsed.origin}/swagger/${swaggerVersion}/swagger.json`;
  } catch {
    const root = normalizedApiUrl.replace(/\/api\/v[^/]+\/orr$/i, '');
    return `${root}/swagger/${swaggerVersion}/swagger.json`;
  }
};

const getSwaggerPaths = (payload: unknown): string[] => {
  if (!payload) return [];

  if (typeof payload === 'string') {
    try {
      return getSwaggerPaths(JSON.parse(payload));
    } catch {
      return [];
    }
  }

  if (typeof payload !== 'object') return [];

  const paths = (payload as { paths?: unknown }).paths;
  if (!paths || typeof paths !== 'object') return [];

  return Object.keys(paths as Record<string, unknown>);
};

export const isReleaseNotesEnabledBySwagger = async () => {
  try {
    const url = getConformanceSwaggerUiJsonUrl();
    const response = await conformanceApi.get<unknown>(url, {
      headers: {
        accept: 'application/json',
      },
    });
    const paths = getSwaggerPaths(response.data);

    return paths.some((path) =>
      path.toLowerCase().includes('release-notes'),
    );
  } catch {
    return false;
  }
};

export const fetchReleaseNotesAdmin = () => {
  return conformanceApi.get<ReleaseNotePage[]>(`${API_URL}/release-notes`);
};

export const fetchReleaseNotesPublic = () => {
  return conformanceApi.get<ReleaseNotePage[]>(`${API_URL}/release-notes/public`);
};

export const createReleaseNote = (data: ReleaseNoteUpsertRequest) => {
  return conformanceApi.post<ReleaseNotePage>(`${API_URL}/release-notes`, data);
};

export const updateReleaseNote = (
  id: string,
  data: ReleaseNoteUpsertRequest,
) => {
  return conformanceApi.put<ReleaseNotePage>(`${API_URL}/release-notes/${id}`, data);
};

export const deleteReleaseNote = (id: string) => {
  return conformanceApi.delete<void>(`${API_URL}/release-notes/${id}`);
};

export const getSystemCheckpointConfigurations = () => {
  const url = `${API_URL.replace('/orr', '')}/system/checkpoints`;
  return conformanceApi.get<{
    message: string;
    status: string;
    data: SystemCheckpointConfiguration[];
  }>(url);
};

export const createSystemCheckpointConfigurations = (
  data: SystemCheckpointConfiguration[] | SystemCheckpointConfiguration,
) => {
  const url = `${API_URL.replace('/orr', '')}/system/checkpoints`;
  return conformanceApi.post(url, data);
};

export const updateSystemCheckpointConfiguration = (data: {
  checkpointId: string;
  checkpointVersion?: string | number;
  status: CheckpointSystemStatus;
  reason: string;
  startDate?: string;
  endDate?: string;
  disabledStartDate?: string;
  disabledReason?: string;
}): Promise<AxiosResponse> => {
  const urlBase = `${API_URL.replace('/orr', '')}/system/checkpoints/${encodeURIComponent(
    data.checkpointId,
  )}`;
  const hasVersion = hasCheckpointVersionValue(data.checkpointVersion);

  const versionQuery = hasVersion
    ? `?version=${encodeURIComponent(`${data.checkpointVersion}`)}`
    : '';

  const url = `${urlBase}${versionQuery}`;

  const nowIso = new Date().toISOString();

  if (data.status === 'LIVE') {
    return conformanceApi.put(url, {
      status: {
        unScored: {},
        disabled: {},
      },
    });
  }

  if (data.status === 'PREVIEW') {
    return conformanceApi.put(url, {
      status: {
        unScored: {
          reason: data.reason,
          startDate: data.startDate,
          endDate: data.endDate,
        },
        disabled: {},
      },
    });
  }

  if (data.status === 'MAINTENANCE') {
      const maintenanceReason = /-\s*maintenance\s*$/i.test(data.reason)
        ? data.reason
        : `${data.reason} - Maintenance`;

      return conformanceApi.put(url, {
        status: {
          unScored: {
            reason: maintenanceReason,
            startDate: data.startDate,
            endDate: data.endDate,
          },
          disabled: {
            reason:
              data.disabledReason ||
              'Disabling checkpoint since maintenance window has ended',
            startDate: data.disabledStartDate || data.endDate,
          },
        },
      });
  }

  return conformanceApi.put(url, {
    status: {
      unScored: {},
      disabled: {
        reason: data.reason,
        startDate: nowIso,
      },
    },
  });
};

export const createApplicationConfiguration = ({
  bitId,
  configurationId,
}: {
  bitId: string;
  configurationId?: string;
}) => {
  return conformanceApi.post<{
    data: ApplicationConfiguration[];
    message: string;
    status: string;
  }>(`${API_URL}/${bitId}/configurations`, null, {
    params: { SourceConfigurationId: configurationId },
  });
};

export const updateCheckpointParameters = ({
  bitId,
  configurationId,
  checkpointId,
  checkpointVersion,
  parameters,
}: CheckpointParametersRequest) => {
  const hasCheckpointVersion = hasCheckpointVersionValue(checkpointVersion);

  const endpoint = hasCheckpointVersion
    ? `${API_URL}/${bitId}/configurations/${configurationId}/checkpoints/${checkpointId}/versions/${encodeURIComponent(
        `${checkpointVersion}`,
      )}/parameters`
    : `${API_URL}/${bitId}/configurations/${configurationId}/checkpoints/${checkpointId}/parameters`;

  return conformanceApi.post<ApplicationConfiguration>(
    endpoint,
    parameters,
  );
};

export const saveApplicationConfiguration = ({
  bitId,
  configurationId,
}: {
  bitId: string;
  configurationId: string;
}) => {
  return conformanceApi.put<{
    data: ApplicationConfiguration[];
    message: string;
    status: string;
  }>(`${API_URL}/${bitId}/configurations/${configurationId}`, {
    isDraft: false,
  });
};

export const deleteApplicationConfiguration = ({
  bitId,
  configurationId,
}: {
  bitId: string;
  configurationId: string;
}) => {
  return conformanceApi.delete<{
    data: ApplicationConfiguration[];
    message: string;
    status: string;
  }>(`${API_URL}/${bitId}/configurations/${configurationId}`);
};

export const fetchEvaluation = async ({
  evaluationId,
  view,
}: {
  evaluationId: string;
  view: string;
}) => {
  const enrichEvaluation = (evaluation: Evaluation | undefined) => {
    if (!evaluation) return;

    evaluation.bitId =
      evaluation.bitId ||
      evaluation.metadata?.bitId ||
      evaluation.request?.bitId ||
      '';
    evaluation.applicationName =
      evaluation.applicationName || evaluation.metadata?.name || '';
    evaluation.divisionName =
      evaluation.divisionName || evaluation.metadata?.division || '';
    evaluation.environmentNames =
      evaluation.environmentNames?.length > 0
        ? evaluation.environmentNames
        : (evaluation.request?.environmentNames ?? []);
    evaluation.platform =
      evaluation.platform ||
      evaluation.request?.headers?.['x-orr-automation-platform'] ||
      '';
    evaluation.args = evaluation.args || evaluation.request?.args || {};
    evaluation.cloudAccounts = Array.isArray(evaluation.cloudAccounts)
      ? evaluation.cloudAccounts
      : [];
    evaluation.extraSourceCloudAccounts = Array.isArray(
      evaluation.extraSourceCloudAccounts,
    )
      ? evaluation.extraSourceCloudAccounts
      : [];
    evaluation.checkpoints = Array.isArray(evaluation.checkpoints)
      ? evaluation.checkpoints.map(normalizeCheckpoint)
      : [];
    if (!evaluation?.extraSourceCloudAccounts) return;

    const hasAccounts = evaluation.extraSourceCloudAccounts.length > 0;
    evaluation.isWKTenant =
      hasAccounts &&
      evaluation.extraSourceCloudAccounts.every(
        (acc) =>
          acc.parentCloudAccountId ===
            '8ac76c91-e7f1-41ff-a89c-3553b2da2c17' ||
          acc.parentCloudAccountId === '297538631377',
      );
  };

  const getEvaluation = (requestedView: string) =>
    conformanceApi.get<Evaluation>(`${API_URL}/evaluations/${evaluationId}`, {
      params: { Format: 'Json', view: requestedView },
    });

  try {
    const response = await getEvaluation(view);
    enrichEvaluation(response.data);
    return response;
  } catch (error: any) {
    if (error.response?.status === 404 && view !== 'default') {
      // The evaluation exists, but the requested view has no checkpoints in it
      // (e.g. a newly-created/empty view, or a view that postdates this
      // evaluation). Reuse the evaluation header from the default view but show
      // an EMPTY checkpoint set — instead of falling back to the default view's
      // own checkpoints, which would misleadingly render the wrong (previous)
      // data for the selected view.
      const base = await getEvaluation('default');
      enrichEvaluation(base.data);
      if (base.data) {
        base.data.checkpoints = [];
        base.data.score = 0;
      }
      return base;
    }

    if (error.response?.status === 404) {
      return {
        data: undefined,
      };
    }

    throw error;
  }
};
 
